import React, { useEffect, useState, useRef } from 'react';
import { ApiService } from '../../api/ProjectInfoService';
import { MetadataService, DocumentType } from '../../Services/MetadataService';
import { ToastContainer, toast } from 'react-toastify';
import { Spinner } from '@fluentui/react-components';
import DynamicTable from '../tables/DynamicTable';
import "./fileuploader.scss";

const BestandenBox = ({ title, project }: any) => {
    const [files, setFiles] = useState<any[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
    const [selectedDocType, setSelectedDocType] = useState<string>("");
    const [isLoadingFiles, setIsLoadingFiles] = useState(true);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        getFiles(); // eslint-disable-line react-hooks/exhaustive-deps
        MetadataService.getDocumentTypes().then((types) => {
            setDocumentTypes(types);
        }).catch((error: any) => {
            console.warn("Kon document types niet ophalen", error);
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const getFiles = async () => {
        setIsLoadingFiles(true);
        ApiService.getFiles(project?.id).then((response: any) => {
            setFiles(response.data);
        }).catch((error: any) => {
            console.log(error);
            toast.error("Er is iets misgegaan bij het ophalen van de bestanden");
        }).finally(() => {
            setIsLoadingFiles(false);
        });
    };

    const uploadFile = async (file: File) => {
        setIsUploading(true);
        const reader = new FileReader();
        const blob = await fetch(URL.createObjectURL(file)).then(r => r.blob());
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
            const base64data = (reader.result as string).split(',')[1];
            try {
                await ApiService.uploadFile(project.id, base64data, file.name, file.type, selectedDocType || undefined);
                toast.success("Bestand is succesvol geupload");
                setSelectedFile(null);
                setSelectedDocType("");
                await getFiles();
            } catch (error) {
                console.log(error);
                toast.error("Er is iets misgegaan bij het uploaden van het bestand");
            } finally {
                setIsUploading(false);
            }
        };
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) setSelectedFile(file);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setSelectedDocType("");
        }
        if (inputRef.current) inputRef.current.value = '';
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <>
        <div className="box">
            <h3>{title}</h3>
            {isLoadingFiles ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "24px", width: "100%", minWidth: 0, boxSizing: "border-box" }}>
                    <Spinner size="medium" label="Bestanden laden..." labelPosition="below" />
                </div>
            ) : (
                <DynamicTable
                    projectId={project?.id}
                    data={files}
                    editable={true}
                    downloadNeeded={true}
                    columns={[
                        { id: 10, label: "Naam",          property: 'name' },
                        { id: 3,  label: "Bestandsnaam",  property: 'fileName' },
                        { id: 4,  label: "Document Type", property: 'documenttype.name' },
                        { id: 13, label: "Id",             property: 'id' },
                        { id: 12, label: "mimeType",       property: 'mimeType' },
                        { id: 14, label: "fileContent",    property: 'fileContent' },
                    ]}
                />
            )}

            <div className="upload-zone-wrapper">
                {!selectedFile ? (
                    <div
                        className={`upload-dropzone${isDragging ? ' upload-dropzone--active' : ''}`}
                        onClick={() => inputRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                    >
                        <div className="upload-dropzone__icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 16V8M12 8L9 11M12 8L15 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M8 20H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <span className="upload-dropzone__label">
                            Sleep een bestand hierheen of <strong>klik om te kiezen</strong>
                        </span>
                    </div>
                ) : (
                    <div className="upload-preview">
                        <div className="upload-preview__info">
                            <div className="upload-preview__file-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                                    <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <div className="upload-preview__details">
                                <span className="upload-preview__name">{selectedFile.name}</span>
                                <span className="upload-preview__size">{formatFileSize(selectedFile.size)}</span>
                            </div>
                        </div>
                        <div className="upload-preview__doctype">
                            <select
                                className="upload-doctype-select"
                                value={selectedDocType}
                                onChange={e => setSelectedDocType(e.target.value)}
                                disabled={isUploading}
                            >
                                <option value="">-- Document type --</option>
                                {documentTypes.map((dt) => (
                                    <option key={dt.id} value={dt.id}>{dt.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="upload-preview__actions">
                            <button
                                className="upload-btn upload-btn--cancel"
                                onClick={() => { setSelectedFile(null); setSelectedDocType(""); }}
                                disabled={isUploading}
                            >
                                Annuleer
                            </button>
                            <button
                                className="upload-btn upload-btn--upload"
                                onClick={() => uploadFile(selectedFile)}
                                disabled={isUploading || !selectedDocType}
                            >
                                {isUploading ? 'Bezig...' : 'Uploaden'}
                            </button>
                        </div>
                    </div>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileInput}
                />
            </div>
        </div>
        <ToastContainer />
        </>
    );
};

export default BestandenBox;
